import React, { useEffect, useState } from 'react';
import {
  Card,
  Form,
  Input,
  Switch,
  Button,
  Space,
  message,
  Typography,
} from 'antd';
import { CheckOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { teamService, Team, CreateTeamData } from '../services/teams';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';

const { Title } = Typography;

export const TeamFormPage: React.FC = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [form] = Form.useForm<CreateTeamData>();
  const [submitting, setSubmitting] = useState(false);
  const [loadingTeam, setLoadingTeam] = useState(false);
  const [teams, setTeams] = useState<Team[]>([]);
  const [isEditing, setIsEditing] = useState(!id);
  const { user } = useAuth();
  const canUpdate = hasPermission(user, 'settings.update');
  const canDelete = hasPermission(user, 'settings.delete');
  const navigate = useNavigate();

  useEffect(() => {
    loadTeams();
    if (isEdit && id) {
      loadTeam(parseInt(id, 10));
      setIsEditing(false);
    } else {
      form.setFieldsValue({ enabled: true });
      setIsEditing(true);
    }
  }, [isEdit, id]);

  const loadTeams = async () => {
    try {
      const response = await teamService.getAll();
      const data = response?.data ?? response ?? [];
      setTeams(Array.isArray(data) ? data : []);
    } catch {
      message.error('Failed to load teams');
    }
  };

  const loadTeam = async (teamId: number) => {
    setLoadingTeam(true);
    try {
      const team = await teamService.getById(teamId);
      form.setFieldsValue({
        name: team.name,
        contact_number: team.contact_number ?? undefined,
        email: team.email ?? undefined,
        enabled: team.enabled,
      });
    } catch {
      message.error('Failed to load team');
      navigate('/teams');
    } finally {
      setLoadingTeam(false);
    }
  };

  const handleSubmit = async (values: CreateTeamData) => {
    setSubmitting(true);
    try {
      const payload = {
        ...values,
        enabled: values.enabled ?? true,
      };
      if (isEdit && id) {
        await teamService.update(parseInt(id, 10), payload);
        message.success('Team updated successfully');
        setIsEditing(false);
      } else {
        await teamService.create(payload);
        message.success('Team created successfully');
      }
      navigate('/teams');
    } catch {
      message.error(isEdit ? 'Failed to update team' : 'Failed to create team');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    if (!window.confirm('Are you sure you want to delete this team?')) return;
    try {
      await teamService.delete(parseInt(id, 10));
      message.success('Team deleted');
      navigate('/teams');
    } catch {
      message.error('Failed to delete team');
    }
  };

  return (
      <Card bordered={false} bodyStyle={{ padding: 0 }} style={{ boxShadow: 'none' }}>
        {isEdit && form.getFieldValue('name') && (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 24,
            paddingBottom: 16,
            borderBottom: '1px solid #f0f0f0',
          }}>
            <Title level={2} style={{ margin: 0, color: '#1890ff', fontSize: 28, fontWeight: 600 }}>
              {form.getFieldValue('name')}
            </Title>
            <Space>
              {!isEditing && canUpdate && (
                <Button onClick={() => setIsEditing(true)}>
                  Edit
                </Button>
              )}
              {isEdit && isEditing && canDelete && (
                <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
                  Delete
                </Button>
              )}
            </Space>
          </div>
        )}

        {!isEdit && (
          <Title level={3} style={{ marginBottom: 24 }}>
            New Team
          </Title>
        )}

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          disabled={loadingTeam || (isEdit && !canUpdate) || (isEdit && !isEditing)}
        >
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '12px', width: '200px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                    <strong>Team Name</strong>
                  </td>
                  <td style={{ padding: '12px', verticalAlign: 'top' }}>
                    {isEdit && !isEditing ? (
                      <div>{form.getFieldValue('name') || '-'}</div>
                    ) : (
                      <Form.Item
                        name="name"
                        rules={[
                          { required: true, message: 'Please enter team name' },
                          {
                            validator: (_, value) => {
                              const name = (value || '').trim().toLowerCase();
                              if (!name) return Promise.resolve();
                              const exists = teams.some((team) => {
                                if (isEdit && team.id === Number(id)) return false;
                                return (team.name || '').trim().toLowerCase() === name;
                              });
                              return exists ? Promise.reject(new Error('Team name already exists')) : Promise.resolve();
                            },
                          },
                        ]}
                        style={{ margin: 0 }}
                      >
                        <Input placeholder="Enter team name" />
                      </Form.Item>
                    )}
                  </td>
                </tr>
                <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '12px', width: '200px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                    <strong>Contact Number</strong>
                  </td>
                  <td style={{ padding: '12px', verticalAlign: 'top' }}>
                    {isEdit && !isEditing ? (
                      <div>{form.getFieldValue('contact_number') || '-'}</div>
                    ) : (
                      <Form.Item name="contact_number" style={{ margin: 0 }}>
                        <Input placeholder="Enter contact number" />
                      </Form.Item>
                    )}
                  </td>
                </tr>
                <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '12px', width: '200px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                    <strong>Email</strong>
                  </td>
                  <td style={{ padding: '12px', verticalAlign: 'top' }}>
                    {isEdit && !isEditing ? (
                      <div>{form.getFieldValue('email') || '-'}</div>
                    ) : (
                      <Form.Item
                        name="email"
                        rules={[{ type: 'email', message: 'Enter a valid email' }]}
                        style={{ margin: 0 }}
                      >
                        <Input placeholder="Enter email" type="email" />
                      </Form.Item>
                    )}
                  </td>
                </tr>
                <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '12px', width: '200px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                    <strong>Enabled</strong>
                  </td>
                  <td style={{ padding: '12px', verticalAlign: 'top' }}>
                    {isEdit && !isEditing ? (
                      <div>{form.getFieldValue('enabled') ? 'Yes' : 'No'}</div>
                    ) : (
                      <Form.Item name="enabled" valuePropName="checked" initialValue style={{ margin: 0 }}>
                        <Switch />
                      </Form.Item>
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

        </Form>
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid #f0f0f0',
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          marginTop: 24,
        }}>
          <Space>
            <Button onClick={() => {
              if (isEdit && !isEditing) {
                navigate('/settings?tab=teams');
                return;
              }
              if (isEdit) {
                setIsEditing(false);
                if (id) {
                  loadTeam(parseInt(id, 10));
                }
              } else {
                navigate('/teams');
              }
            }}>
              Cancel
            </Button>
            {(!isEdit || canUpdate) && isEditing && (
              <Button type="primary" htmlType="submit" loading={submitting}>
                {isEdit ? 'Save' : 'Create'}
              </Button>
            )}
          </Space>
        </div>
      </Card>
  );
};
